import React from "react";
import { useIntl } from "react-intl";
import { Breadcrumb } from "antd";
import { Link } from "react-router-dom";
import { checkPermissions } from "@comps/commonFunctions";
import "./layouts-styles.css";

const ENTITY_OVERRIDES = {
  "my space": "Request",
  "my roster": "Roster",
  "company roster": "Roster",
  "company roaster": "Roster",
  "my roaster": "Roster",
  "subordinate cases": "Case",
  "case sheets": "Case",
  "case sheet": "Case",
  "end of employments": "End of Employment",
  "ip restrictions": "IP Restriction",
  "tax structures": "Tax Structure",
  "all reports": "Report",
  "saleries": "Salary",
  "kpi": "KPI",
  "loan and advance categories": "Loan And Advance Category",
};

// Plural page titles ("Locations", "Branches", "Addresses") name a single record
// on the create button, so trim the plural without mangling words that only look
// plural ("Marital Status", "Gratuity", "Address").
const singularize = (value) => {
  if (/(ss|us|is)$/i.test(value)) {
    return value;
  }

  if (/ies$/i.test(value)) {
    return value.replace(/ies$/i, "y");
  }

  if (/sses$/i.test(value)) {
    return value.replace(/sses$/i, "ss");
  }

  if (/uses$/i.test(value)) {
    return value.replace(/uses$/i, "us");
  }

  if (/(ch|sh|x|z)es$/i.test(value)) {
    return value.replace(/es$/i, "");
  }

  return value.replace(/s$/i, "");
};

// Titles are usually `trans(key)` elements (FormattedMessage), which carry the
// label in the `id` prop instead of in children, so resolve those through intl.
const getTextFromNode = (node, intl) => {
  if (node === null || node === undefined || typeof node === "boolean") {
    return "";
  }

  if (typeof node === "string" || typeof node === "number") {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map((each) => getTextFromNode(each, intl)).filter(Boolean).join(" ");
  }

  if (React.isValidElement(node)) {
    const fromChildren = getTextFromNode(node.props.children, intl);
    if (fromChildren) {
      return fromChildren;
    }

    const messageId = node.props.id;
    if (intl && messageId && intl.messages?.[messageId]) {
      return intl.formatMessage({ id: messageId });
    }

    return node.props.defaultMessage || "";
  }

  return "";
};

const titleToCreateEntity = (title = "", intl) => {
  const normalized = getTextFromNode(title, intl)
    .replace(/\s+/g, " ")
    .replace(/:\s*\d+.*$/, "")
    .trim();

  if (!normalized) {
    return "Record";
  }

  const key = normalized.toLowerCase();
  if (ENTITY_OVERRIDES[key]) {
    return ENTITY_OVERRIDES[key];
  }

  return singularize(normalized.replace(/^company\s+/i, "").replace(/^my\s+/i, ""));
};

const getCreateEntity = (headers = {}, intl) => headers.createEntity || headers.actionEntity || titleToCreateEntity(headers.title, intl);

const BaseHeader = (props) => {
  const { children, headers, modkey, className } = props;
  const intl = useIntl();
  const permissionAwareButtonNames = new Set([
    "BaseButton",
    "BaseCreate",
    "BaseImport",
    "BaseDownload",
    "BaseCustomButton",
  ]);

  const attachModkey = (child) => {
    if (!React.isValidElement(child)) {
      return child;
    }

    if (child.type === React.Fragment) {
      return React.cloneElement(child, {
        children: React.Children.map(child.props.children, attachModkey),
      });
    }

    const componentName = child.type?.displayName || child.type?.name;
    const isCreateButton = componentName === "BaseCreate";
    const createButtonText = String(child.props.text || "");
    const isNavigationButton = ["Back", "Refresh"].includes(createButtonText);
    const isDefaultCreateAction = !isCreateButton || !isNavigationButton;
    const inferredCreateText = isCreateButton && !child.props.text
      ? { text: `Add ${getCreateEntity(headers, intl)}` }
      : {};

    return permissionAwareButtonNames.has(componentName) && isDefaultCreateAction && child.props.modkey === undefined && modkey
      ? React.cloneElement(child, { modkey, ...inferredCreateText })
      : isCreateButton && !child.props.text
        ? React.cloneElement(child, inferredCreateText)
      : child;
  };

  const getChildren = () => {
    // Remove Create button if user does not have create permission
    if (children === undefined || !children) {
      return null;
    }

    let actionItems = React.Children.toArray(children).filter(Boolean).map(attachModkey);
    let index = null;

    actionItems.forEach((each, key) => {
      if (
        each.props !== undefined &&
        each.props.onClick !== undefined &&
        each.props.onClick.name === "onCreate"
      ) {
        if (!checkPermissions(modkey, "create")) {
          index = key;
        }
      }
    });

    if (props.moduleKey === "customPayitem") {
      index = null;
    }

    if (index !== null) {
      actionItems = actionItems.slice(0, index).concat(actionItems.slice(index + 1));
    }

    return actionItems.length ? actionItems : null;
  };

  const renderBreadcrumb = () => {
    if (!headers?.breadcrumb?.length) {
      return null;
    }

    return (
      <Breadcrumb className="base-header__breadcrumb">
        { headers.breadcrumb.map((each, index) => {
          const label = each.path ? <Link to={ each.path }>{ each.name }</Link> : each.name;

          return <Breadcrumb.Item key={ each.path || `${each.name}-${index}` }>{ label }</Breadcrumb.Item>;
        }) }
      </Breadcrumb>
    );
  };

  const actions = getChildren();
  const subtitle = headers?.subtitle || headers?.description;
  const breadcrumb = renderBreadcrumb();
  const hasTitleRow = Boolean(headers?.title || subtitle);
  const hasTitleBlock = Boolean(breadcrumb || hasTitleRow);

  // An empty title still rendered an <h1>, so pages that pass title: "" showed a
  // blank band that only consumed vertical space. Render nothing when there is
  // nothing to show.
  if (!hasTitleBlock && !actions) {
    return null;
  }

  return (
    <div className={ ["base-header da-px-32", className].filter(Boolean).join(" ") }>
      <div className="base-header__surface page-header">
        <div className="base-header__content">
          { hasTitleBlock ? (
            <div className="base-header__title-block">
              { breadcrumb }
              { hasTitleRow ? (
                <div className="base-header__title-row">
                  { headers?.title ? <h1 className="base-header__title">{ headers.title }</h1> : null }
                  { subtitle ? <p className="base-header__subtitle">{ subtitle }</p> : null }
                </div>
              ) : null }
            </div>
          ) : null }

          { actions ? <div className="base-header__actions">{ actions }</div> : null }
        </div>
      </div>
    </div>
  );
};

export default BaseHeader;
